import React from "react"
import { Box, Grid, RadioGroup, FormControlLabel, Radio } from "@mui/material"
import { makeStyles } from "makeStyles"
import Skeleton from "@mui/material/Skeleton"

import Price from "components/Price"
import LazyLoadedImage from "components/LazyLoadedImage"

import { netToGross } from "@secureo/common/utils/tax"

import { ProductConfigurationsBySku } from "@secureo/common/reducers/productDetailPageReducer"
import {
	ConfigurationGroupProduct,
	ConfigurationGroup as ConfigurationGroupInterface,
	ConfigurationGroupProductsBySku,
} from "@secureo/common/typings/Product"
import { CountryCode } from "@secureo/common/typings/CountryCode"

const useStyles = makeStyles()({
	card: {
		background: "white",
		borderRadius: 10,
		boxShadow: " 0px 0px 5px 0px rgba(0,0,0,0.15)",
		transition: "0.3s",
		paddingLeft: 15,
		border: "1px solid whitesmoke",
	},
	grid: {
		display: "flex",
		alignItems: "center",
		"@media only screen and (max-width: 600px)": {
			display: "block",
		},
	},
	image: {
		height: 70,
		width: 70,
		objectFit: "contain",
		mixBlendMode: "multiply",
	},
	background: {
		background: "#EDEFF0",
		padding: 5,
		"@media only screen and (max-width: 600px)": {
			background: "none",
		},
	},
})

const ConfigurationGroupSingleConfiguration = ({
	isLoading,
	configurationProduct,
	grossPriceCents,
	isChecked,
}: ConfigurationGroupSingleConfigurationProps) => {
	const { classes } = useStyles()

	const { sku, name, image, description } = configurationProduct

	return (
		<Grid item xs={12}>
			<Box
				className={classes.card}
				style={{
					border: isChecked ? "1px solid #4691CE" : "1px solid whitesmoke",
					background: isChecked ? "#E7EFF6" : "white",
				}}
			>
				<FormControlLabel
					value={sku}
					control={<Radio color="primary" />}
					label={
						<Box p={2} className={classes.grid}>
							<Box mr={3} className={classes.background}>
								{image ? (
									<LazyLoadedImage
										src={image}
										title={name}
										alt={name}
										className={classes.image}
									/>
								) : (
									<Skeleton
										variant="rectangular"
										width={73}
										height={70}
										animation={false}
									/>
								)}
							</Box>

							<Box pr={1}>
								<strong>
									{name} + <Price priceInEuroCents={grossPriceCents} />
								</strong>
								{description && (
									<div
										style={{ color: "#707070" }}
										dangerouslySetInnerHTML={{
											__html: description,
										}}
									/>
								)}
							</Box>
						</Box>
					}
					labelPlacement="end"
					disabled={isLoading}
				/>
			</Box>
		</Grid>
	)
}

interface ConfigurationGroupSingleConfigurationProps {
	isLoading: boolean
	configurationProduct: ConfigurationGroupProduct
	grossPriceCents: number
	isChecked: boolean
}

const ConfigurationGroupSingle = ({
	isLoading,
	configurationGroup,
	configurationGroupProductsBySku,
	selectedConfigurationsBySku,
	setSingleConfigurationGroupConfiguration,
	taxRate,
	countryCode,
}: Props) => {
	const { name: configurationGroupName, items } = configurationGroup

	const value = Object.values(selectedConfigurationsBySku)?.[0]?.sku ?? items?.[0]?.sku

	const changeConfiguration = (event: React.ChangeEvent<HTMLInputElement>) => {
		const sku = event.target.value

		setSingleConfigurationGroupConfiguration(
			configurationGroupProductsBySku,
			configurationGroupName,
			sku,
		)
	}

	return (
		<RadioGroup
			aria-label={configurationGroupName}
			name={configurationGroupName}
			value={value}
			onChange={changeConfiguration}
		>
			<Grid container spacing={3}>
				{items.map((configurationGroupItem) => {
					const { sku } = configurationGroupItem
					const configurationProduct = configurationGroupProductsBySku?.[sku]

					const netPriceCents =
						configurationProduct?.pricesByCountryCode?.[countryCode] ?? 0
					const grossPriceCents = Math.round(netToGross(netPriceCents, taxRate))

					if (!configurationProduct) return null

					return (
						<ConfigurationGroupSingleConfiguration
							key={sku}
							isLoading={isLoading}
							configurationProduct={configurationProduct}
							grossPriceCents={grossPriceCents}
							isChecked={sku === value}
						/>
					)
				})}
			</Grid>
		</RadioGroup>
	)
}

interface Props {
	isLoading: boolean
	configurationGroup: ConfigurationGroupInterface
	configurationGroupProductsBySku: ConfigurationGroupProductsBySku
	selectedConfigurationsBySku: ProductConfigurationsBySku
	taxRate: number
	countryCode: CountryCode
	setSingleConfigurationGroupConfiguration: (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
	) => void
}

export default ConfigurationGroupSingle
